"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import { UserDesktopSidebar } from "@/components/UserDesktopSidebar";
import { NavigationItem } from "@/components/NavigationItem";
import { ehRotaDriver, ITENS_DRIVER, itemDriverAtivo } from "@/lib/biblioteca-navegacao";
import { usarExperienciaDesktop, WEB_APP } from "@/lib/ambiente-cliente";
import { buscarJsonComCache } from "@/lib/fetch-cache-cliente";
import { useExperienciaProduto } from "@/components/ExperienciaProvider";
import {
  ehAreaAnaliseGestorFollowZap,
  ehAreaGerencialFollowPro,
  hrefModuloFollowZapAtivo,
  itemSubmenuFollowZapAtivo,
  menuOcultaConfiguracoes,
  modulosFollowZapParaContexto,
  submenuFollowZapDoPathname,
  type FollowZapModuloId,
  type ItemSubmenuFollowZap,
} from "@/lib/followzap-navigation";
import type { ExperienciaProduto, RecursoProduto } from "@/lib/experiencia-produto";
import {
  ArrowLeftRight,
  BarChart3,
  Building2,
  BriefcaseBusiness,
  CalendarDays,
  CalendarClock,
  ClipboardList,
  Columns3,
  ContactRound,
  Download,
  FileText,
  Funnel,
  GitBranch,
  Heart,
  HardDrive,
  Trash2,
  UserRound,
  History,
  Home,
  ImageIcon,
  Inbox,
  List,
  MessageSquareText,
  Megaphone,
  PlayCircle,
  Package,
  ShoppingCart,
  Tags,
  Mic,
  Sparkles,
  User,
  UserPlus,
  Users,
  Webhook,
  Wrench,
  type LucideIcon,
} from "lucide-react";

type Papel = "PROGRAMADOR" | "PLATFORM_ADMIN" | "ADM" | "GESTOR" | "USUARIO";
type ModoSistema = "ESSENCIAL" | "AVANCADO";

type NavItem = {
  href: string;
  label: string;
  mobileLabel?: string;
  Icon: LucideIcon;
  desktopOnly?: boolean;
  somenteFollowZap?: boolean;
  ocultarNoFollowZap?: boolean;
  recurso?: RecursoProduto;
  produtos?: readonly ExperienciaProduto["produto"][];
};

const ITENS_USUARIO: NavItem[] = [
  { href: "/", label: "Início", mobileLabel: "Início", Icon: Home, recurso: "inicio" },
  { href: "/profissional/mensagens", label: "Fluxos", mobileLabel: "Fluxos", Icon: Sparkles, recurso: "biblioteca" },
  { href: "/drive", label: "Driver", mobileLabel: "Driver", Icon: Sparkles, produtos: ["FOLLOW_ZAP", "FOLLOW_PRO"] },
  { href: "/follow-up", label: "Follow-up", mobileLabel: "Follow-up", Icon: CalendarClock, recurso: "followUp" },
  { href: "/pipeline", label: "Pipeline", mobileLabel: "Pipeline", Icon: Columns3, recurso: "pipeline" },
  { href: "/acompanhamento", label: "Acompanhamentos", mobileLabel: "Acomp.", Icon: ClipboardList, recurso: "acompanhamento", ocultarNoFollowZap: true },
  { href: "/cadastro-novo", label: "Agendar e Cadastrar", mobileLabel: "Ag./Cad.", Icon: UserPlus, recurso: "clientes", ocultarNoFollowZap: true },
  { href: "/etiquetas", label: "Etiquetas", mobileLabel: "Etiquetas", Icon: Tags, recurso: "etiquetas", somenteFollowZap: true },
  { href: "/crm", label: "CRM", mobileLabel: "CRM", Icon: CalendarDays, recurso: "crm" },
  { href: "/cadastro-lista", label: "Agenda de Retorno", mobileLabel: "Agen. Ret", Icon: FileText, ocultarNoFollowZap: true },
  { href: "/carteira", label: "Carteira", mobileLabel: "Carteira", Icon: BriefcaseBusiness, recurso: "carteira" },
  { href: "/produtos", label: "Produtos", mobileLabel: "Produtos", Icon: Package, recurso: "vendas", produtos: ["FOLLOW_ZAP"] },
  { href: "/aprenda", label: "Aprenda", mobileLabel: "Aprenda", Icon: PlayCircle, recurso: "aprenda" },
  { href: "/captacao", label: "Captação", mobileLabel: "Captação", Icon: Inbox, recurso: "integracoes" },
  { href: "/integracoes", label: "Integrações", mobileLabel: "Integrações", Icon: Wrench, recurso: "integracoes" },
  { href: "/leads", label: "Leads", mobileLabel: "Leads", Icon: ContactRound, recurso: "leads" },
  { href: "/analise", label: "Análise", mobileLabel: "Análise", Icon: BarChart3, recurso: "analise", produtos: ["FOLLOW_ZAP"] },
  { href: "/meta-ads", label: "Meta Ads", mobileLabel: "Meta Ads", Icon: Megaphone, recurso: "anuncios" },
  { href: "/configuracoes", label: "Configurações", mobileLabel: "Config.", Icon: BarChart3, recurso: "configuracoes", desktopOnly: true },
];

const SUBMENU_DRIVER = ITENS_DRIVER.map((item) => ({ ...item, Icon: item.href === "/drive/pessoal" ? UserRound : item.href === "/drive/lixeira" ? Trash2 : HardDrive }));

const ITENS_FLUXOS: Array<{ href: string; label: string; Icon: LucideIcon }> = [
  { href: "/profissional/mensagens", label: "Mensagens", Icon: MessageSquareText },
  { href: "/profissional/audios", label: "Áudios", Icon: Mic },
  { href: "/profissional/midias", label: "Mídias", Icon: ImageIcon },
  { href: "/profissional/funis", label: "Funis", Icon: Funnel },
  { href: "/profissional/favoritos", label: "Favoritos", Icon: Heart },
  { href: "/profissional/etiquetas", label: "Etiquetas", Icon: Tags },
];

const ITENS_CAPTACAO: Array<{ href: string; label: string; Icon: LucideIcon }> = [
  { href: "/captacao/formulario-meta", label: "Formulário Meta", Icon: ContactRound },
  { href: "/captacao/followzap", label: "FollowZap", Icon: Webhook },
];

const ITENS_META_ADS: Array<{ href: string; label: string; Icon: LucideIcon }> = [
  { href: "/meta-ads/campanhas", label: "Campanhas", Icon: Megaphone },
  { href: "/meta-ads/objetivos", label: "Objetivos", Icon: BarChart3 },
  { href: "/meta-ads/contas-de-anuncio", label: "Contas de anúncio", Icon: Wrench },
];

const ITENS_ANALISE: Array<{ href: string; label: string; Icon: LucideIcon }> = [
  { href: "/analise/rendimento", label: "Vendas", Icon: ShoppingCart },
  { href: "/analise/leads", label: "Leads", Icon: Users },
  { href: "/analise/entrada-distribuicao", label: "Distribuição", Icon: GitBranch },
  { href: "/analise/atendimento", label: "Atendimento", Icon: MessageSquareText },
];

const ITENS_LEADS: Array<{ href: string; label: string; Icon: LucideIcon }> = [
  { href: "/leads/lista", label: "Lista", Icon: List },
  { href: "/leads/historico", label: "Histórico", Icon: History },
];

const ICONES_MODULOS_FOLLOWZAP: Record<FollowZapModuloId, LucideIcon> = {
  inicio: Home,
  fluxos: Sparkles,
  driver: Sparkles,
  "follow-up": CalendarClock,
  etiquetas: Tags,
  crm: CalendarDays,
  carteira: BriefcaseBusiness,
  produtos: Package,
  captacao: Inbox,
  integracoes: Wrench,
  leads: ContactRound,
  equipe: Users,
  analise: BarChart3,
  "meta-ads": Megaphone,
  configuracoes: BarChart3,
};

const ICONES_SUBMENUS_FOLLOWZAP: Record<string, LucideIcon> = {
  "/profissional/mensagens": MessageSquareText,
  "/profissional/audios": Mic,
  "/profissional/midias": ImageIcon,
  "/profissional/funis": Funnel,
  "/profissional/favoritos": Heart,
  "/profissional/etiquetas": Tags,
  "/drive/pessoal": UserRound,
  "/drive/meu-driver": HardDrive,
  "/drive/lixeira": Trash2,
  "/captacao/formulario-meta": ContactRound,
  "/captacao/followzap": Webhook,
  "/leads/lista": List,
  "/leads/historico": History,
  "/followpro/equipe/integrantes": Users,
  "/analise/rendimento": ShoppingCart,
  "/analise/leads": Users,
  "/analise/entrada-distribuicao": GitBranch,
  "/analise/atendimento": MessageSquareText,
  "/meta-ads/campanhas": Megaphone,
  "/meta-ads/objetivos": BarChart3,
  "/meta-ads/contas-de-anuncio": Wrench,
};

function adicionarIconesSubmenuFollowZap(itens: readonly ItemSubmenuFollowZap[]) {
  return itens.map((item) => ({ ...item, Icon: ICONES_SUBMENUS_FOLLOWZAP[item.href] }));
}

const ITENS_GESTOR: NavItem[] = [
  { href: "/gestor/inicio", label: "Inicio", Icon: Home, recurso: "inicio" },
  { href: "/gestor/pipeline", label: "Pipeline", Icon: Columns3, recurso: "pipeline" },
  { href: "/gestor/baixar-clientes", label: "Baixar clientes", mobileLabel: "Baixar", Icon: Download, recurso: "downloads" },
  { href: "/gestor/follow-up", label: "Follow-up", Icon: CalendarClock, recurso: "followUp" },
  { href: "/gestor/equipe", label: "Equipe", Icon: Users },
  { href: "/gestor", label: "Usuario", Icon: User },
  { href: "/gestor/solicitar-migracao", label: "Migracao", Icon: ArrowLeftRight },
  { href: "/gestor/aprenda", label: "Aprenda", Icon: PlayCircle, recurso: "aprenda" },
  { href: "/drive", label: "Driver", Icon: Sparkles, produtos: ["FOLLOW_PRO"] },
];

const ITENS_ADM: NavItem[] = [
  { href: "/admin/inicio", label: "Inicio", Icon: Home, recurso: "inicio" },
  { href: "/admin/pipeline", label: "Pipeline", Icon: Columns3, recurso: "pipeline" },
  { href: "/admin/baixar-clientes", label: "Baixar clientes", mobileLabel: "Baixar", Icon: Download, recurso: "downloads" },
  { href: "/admin/equipe", label: "Equipe", Icon: Users },
  { href: "/admin", label: "Usuarios", Icon: Users },
  { href: "/migracao", label: "Migracao", Icon: ArrowLeftRight },
  { href: "/solicitacoes", label: "Solicitacoes", Icon: Inbox },
];

const ITENS_PROGRAMADOR: NavItem[] = [
  { href: "/programador/inicio", label: "Inicio", Icon: Home },
  { href: "/programador", label: "Empresas", Icon: Building2 },
  { href: "/programador/aulas", label: "Aulas", Icon: PlayCircle },
  { href: "/admin", label: "Equipe", Icon: Users },
  { href: "/migracao", label: "Migracao", Icon: ArrowLeftRight },
  { href: "/solicitacoes", label: "Solicitacoes", Icon: Inbox },
];

export function NavBar() {
  const { dados, experiencia, carregado: experienciaCarregada, experienciaConfirmada } = useExperienciaProduto();
  const pathname = usePathname();
  const [papel, setPapel] = useState<Papel | null>(null);
  const [papelCarregado, setPapelCarregado] = useState(false);
  const [temNovidade, setTemNovidade] = useState(false);
  const desktopApp = usarExperienciaDesktop();
  const [sidebarCompacta, setSidebarCompacta] = useState(false);
  const [sidebarOculta, setSidebarOculta] = useState(false);
  const [modoSistema, setModoSistema] = useState<ModoSistema>("AVANCADO");
  const [organizacaoId, setOrganizacaoId] = useState("");

  useEffect(() => {
    queueMicrotask(() => setOrganizacaoId(new URLSearchParams(window.location.search).get("organizacaoId")?.trim() || ""));
  }, [pathname]);

  useEffect(() => {
    if (experiencia.produto !== "FOLLOW_CUSTOM") return;
    let ativo = true;
    const carregarModo = async () => {
      const configuracaoModo = await buscarJsonComCache<{ modo?: ModoSistema }>("/api/modo-sistema", { ttl: 120_000, force: true }).catch(() => ({ modo: "AVANCADO" as const }));
      if (ativo) setModoSistema(configuracaoModo.modo === "ESSENCIAL" ? "ESSENCIAL" : "AVANCADO");
    };

    void carregarModo();
    const timer = setInterval(() => void carregarModo(), 120_000);
    return () => {
      ativo = false;
      clearInterval(timer);
    };
  }, [experiencia.produto]);

  useEffect(() => {
    if (experiencia.produto !== "FOLLOW_CUSTOM") return;
    const atualizarModo = (event: Event) => {
      const modo = (event as CustomEvent<{ modo?: ModoSistema }>).detail?.modo;
      setModoSistema(modo === "ESSENCIAL" ? "ESSENCIAL" : "AVANCADO");
    };
    window.addEventListener("bp:modo-sistema-alterado", atualizarModo as EventListener);
    return () => window.removeEventListener("bp:modo-sistema-alterado", atualizarModo as EventListener);
  }, [experiencia.produto]);

  useEffect(() => {
    let ativo = true;

    const carregarSessao = async () => {
      try {
        const dados = await buscarJsonComCache<{ papel?: Papel }>("/api/me", { ttl: 5 * 60_000 });
        if (!ativo) return;
        setPapel(dados.papel ?? null);
      } catch {
      } finally {
        if (ativo) setPapelCarregado(true);
      }
    };

    void carregarSessao();
    return () => {
      ativo = false;
    };
  }, []);

  const checarNovidades = useCallback(() => {
    if (papel !== "GESTOR" && papel !== "ADM" && papel !== "PROGRAMADOR" && papel !== "PLATFORM_ADMIN") return;
    fetch("/api/migracao-solicitacoes/novidades")
      .then((r) => r.json())
      .then((d) => setTemNovidade(!!d.temNovidade))
      .catch(() => {});
  }, [papel]);

  useEffect(() => {
    checarNovidades();
  }, [pathname, checarNovidades]);

  useEffect(() => {
    const limpar = () => setTemNovidade(false);
    const rechecar = () => checarNovidades();

    window.addEventListener("solicitacoes-vistas", limpar);
    window.addEventListener("focus", rechecar);

    return () => {
      window.removeEventListener("solicitacoes-vistas", limpar);
      window.removeEventListener("focus", rechecar);
    };
  }, [checarNovidades]);

  useEffect(() => {
    const syncSidebar = (event: Event) => {
      const detail = (event as CustomEvent<{ compacta?: boolean; oculta?: boolean }>).detail;
      setSidebarCompacta(!!detail?.compacta);
      setSidebarOculta(!!detail?.oculta);
    };

    window.addEventListener("bp:desktop-sidebar-compact", syncSidebar as EventListener);
    return () => {
      window.removeEventListener("bp:desktop-sidebar-compact", syncSidebar as EventListener);
    };
  }, []);

  const modoEquipeGestor = papel === "GESTOR" && pathname.startsWith("/gestor");
  const usuarioDesktop = papel === "USUARIO" || (papel === "GESTOR" && !modoEquipeGestor);
  const sidebarCompactaAppUsuario =
    !WEB_APP && desktopApp && (pathname === "/carteira" || pathname === "/aprenda" || pathname === "/ferramenta" || (sidebarCompacta && (pathname === "/acompanhamento" || pathname === "/cadastro-novo" || pathname === "/crm")));
  const sidebarOcultaAppUsuario = (desktopApp && sidebarOculta && (pathname === "/acompanhamento" || pathname === "/cadastro-novo" || pathname === "/crm" || pathname === "/ferramenta")) || (WEB_APP && pathname === "/profissional/funis" && sidebarOculta);
  const shellWebIntegrado = WEB_APP && usuarioDesktop;
  const recursosProfissionaisLiberados = experiencia.produto !== "FOLLOW_CUSTOM" || modoSistema === "AVANCADO";
  const areaGerencialAnalise = ehAreaGerencialFollowPro(pathname, experiencia.produto, papel);
  const areaAnaliseGestorFollowZap = ehAreaAnaliseGestorFollowZap(pathname, experiencia.produto, papel);
  const modulosFollowZap = experienciaCarregada && experienciaConfirmada
    ? modulosFollowZapParaContexto({
        produto: experiencia.produto,
        papel,
        recursos: experiencia.recursos,
        modoAvancado: recursosProfissionaisLiberados,
        mostrarFollowUp: papel === "USUARIO",
        areaAnaliseGestorFollowZap,
        areaGerencialAnalise,
        modulosFollowProUsuario: dados?.modulosFollowProUsuario,
      })
    : null;
  const usandoCatalogoFollowZap = modulosFollowZap !== null;
  const itensFollowZap: NavItem[] = modulosFollowZap?.map((item) => ({
    href: item.href,
    label: item.label,
    mobileLabel: item.mobileLabel,
    Icon: ICONES_MODULOS_FOLLOWZAP[item.id],
    desktopOnly: "desktopOnly" in item ? item.desktopOnly : undefined,
  })) ?? [];
  const itensBase =
    usandoCatalogoFollowZap
      ? itensFollowZap
      : papel === "PROGRAMADOR" || papel === "PLATFORM_ADMIN"
      ? ITENS_PROGRAMADOR
      : papel === "ADM"
        ? ITENS_ADM
        : modoEquipeGestor
          ? ITENS_GESTOR
          : ITENS_USUARIO;
  const itensContextuais = itensBase;
  const itens = experienciaCarregada && experienciaConfirmada
    ? usandoCatalogoFollowZap
      ? itensContextuais
      : itensContextuais
      .filter(({ href, recurso, produtos, somenteFollowZap, ocultarNoFollowZap }) =>
        (href !== "/pipeline" || (experienciaCarregada && experiencia.recursos.pipeline)) &&
        (!produtos || produtos.includes(experiencia.produto)) &&
        (!recurso || experiencia.recursos[recurso]) &&
        (href !== "/analise" || papel === "USUARIO" || papel === "GESTOR") &&
        (href !== "/follow-up" || (papel === "USUARIO" || papel === "GESTOR")) &&
        (recursosProfissionaisLiberados || !href.startsWith("/profissional")) &&
        (!ocultarNoFollowZap || experiencia.produto !== "FOLLOW_ZAP") &&
        (!somenteFollowZap || (experiencia.produto === "FOLLOW_ZAP" && papel === "USUARIO")) &&
        (href !== "/configuracoes" || !menuOcultaConfiguracoes(experiencia.produto, papel))
      )
      .map((item) => item.href === "/profissional/mensagens" && experiencia.produto === "FOLLOW_CUSTOM"
        ? { ...item, label: "Profissional", mobileLabel: "Pro" }
        : item)
    : [];

  const hrefAtivo = usandoCatalogoFollowZap
    ? hrefModuloFollowZapAtivo(pathname, itens)
    : itens
      .filter(({ href }) => pathname === href || (href !== "/" && pathname.startsWith(href + "/")))
      .sort((a, b) => b.href.length - a.href.length)[0]?.href;

  const badgeHref =
    papel === "GESTOR"
      ? "/gestor/solicitar-migracao"
      : papel === "ADM" || papel === "PROGRAMADOR" || papel === "PLATFORM_ADMIN"
        ? "/solicitacoes"
        : null;

  const papelLabel =
    papel === "GESTOR"
      ? "Gestao"
      : papel === "ADM"
        ? "Diretoria"
        : papel === "PROGRAMADOR" || papel === "PLATFORM_ADMIN"
          ? "Plataforma"
          : "Carteira";

  const itensMobile = itens.filter((item) => !item.desktopOnly);
  const hrefNavegacao = (href: string) => organizacaoId && href.startsWith("/admin")
    ? `${href}?organizacaoId=${encodeURIComponent(organizacaoId)}`
    : href;
  const pilotoMobileSidebar = (papel === "USUARIO" && (experiencia.produto === "FOLLOW_ZAP" || experiencia.produto === "FOLLOW_PRO")) || areaAnaliseGestorFollowZap || areaGerencialAnalise;
  const submenuFollowZap = usandoCatalogoFollowZap
    ? submenuFollowZapDoPathname(pathname, "mobile", modulosFollowZap ?? [])
    : null;
  const submenuDriver = usandoCatalogoFollowZap
    ? submenuFollowZap?.moduloId === "driver"
    : ehRotaDriver(pathname) && itens.some((item) => item.href === "/drive");
  const submenuMobile = usandoCatalogoFollowZap
    ? submenuFollowZap ? adicionarIconesSubmenuFollowZap(submenuFollowZap.itens) : null
    : submenuDriver ? SUBMENU_DRIVER : pathname.startsWith("/profissional/") ? ITENS_FLUXOS : pathname.startsWith("/captacao/") ? ITENS_CAPTACAO : pathname.startsWith("/meta-ads/") ? ITENS_META_ADS : pathname.startsWith("/leads/") ? ITENS_LEADS : pathname === "/analise" || pathname.startsWith("/analise/") ? ITENS_ANALISE : null;

  if (!papelCarregado || !experienciaCarregada || !experienciaConfirmada) {
    return (
      <>
        <aside className="hidden lg:flex lg:w-[203px] lg:flex-shrink-0" />
        <nav className="fixed bottom-0 left-1/2 z-40 w-full max-w-[430px] -translate-x-1/2 border-t border-border bg-card lg:hidden">
          <div className="h-[52px]" aria-hidden="true" />
        </nav>
      </>
    );
  }

  return (
    <>
      <aside className={`hidden lg:flex lg:flex-shrink-0 ${usuarioDesktop ? (sidebarOcultaAppUsuario ? "lg:w-0 lg:overflow-hidden" : sidebarCompactaAppUsuario ? "lg:w-[92px]" : "lg:w-[185px]") : "lg:w-[203px]"}`}>
        {usuarioDesktop ? (
          <UserDesktopSidebar activeHref={hrefAtivo} sticky compact={sidebarCompactaAppUsuario} integrado={shellWebIntegrado} modoAvancado={recursosProfissionaisLiberados} mostrarFollowUp={usuarioDesktop} papel={papel ?? undefined} />
        ) : (
          <div className="sticky top-8 flex w-full flex-col rounded-[24px] border border-white/70 bg-[linear-gradient(180deg,#FFFFFF_0%,#F8FAFF_100%)] px-4 py-5 shadow-[0_24px_60px_rgba(15,23,42,0.08)]">
            <div className="mb-5 flex items-center gap-2 border-b border-[#E8EDF5] pb-4">
              <div
                className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-[14px]"
                style={{ background: "linear-gradient(135deg, #2563EB 0%, #1d4ed8 100%)" }}
              >
                <span className="text-sm font-extrabold tracking-tight text-white">{experiencia.marca.sigla}</span>
              </div>
              <div className="min-w-0">
                <p className="truncate text-[13px] font-bold text-navy">{experiencia.marca.nome}</p>
                <p className="text-[11px] text-muted">{papelLabel}</p>
              </div>
            </div>

            <div className="space-y-2">
              {itens.map(({ href, label, Icon }) => {
                const ativo = href === hrefAtivo;
                const mostrarPonto = temNovidade && href === badgeHref;

                return (
                  <Link
                    key={href}
                    href={hrefNavegacao(href)}
                    prefetch
                    className={`flex h-9 items-center gap-2 rounded-[12px] px-2.5 text-[12.5px] transition-colors ${
                      ativo ? "bg-blue-50 text-brand" : "text-muted hover:bg-[#F5F7FB] hover:text-navy"
                    }`}
                  >
                    <span className={`relative flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-[9px] ${ativo ? "bg-white" : "bg-white/70"}`}>
                      <Icon className={`h-4 w-4 ${ativo ? "text-brand" : "text-muted"}`} />
                      {mostrarPonto && (
                        <span className="absolute right-1.5 top-1.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-red-500" />
                      )}
                    </span>
                    <span className="min-w-0 truncate font-semibold">{label}</span>
                  </Link>
                );
              })}
            </div>

          </div>
        )}
      </aside>

      <nav
        data-user-mobile-navigation={pilotoMobileSidebar ? "true" : undefined}
        className={`fixed bottom-0 left-1/2 z-40 w-full max-w-[430px] -translate-x-1/2 border-t lg:hidden ${pilotoMobileSidebar ? "border-[var(--fz-sidebar-border)] bg-[var(--fz-sidebar-bg)]" : "border-border bg-card"}`}
      >
        {submenuMobile ? (
          <div className={`absolute bottom-full left-0 w-full border shadow-lg ${pilotoMobileSidebar ? "border-[var(--fz-sidebar-border)] bg-[var(--fz-sidebar-bg)]" : "border-border bg-card"}`}>
            <div className="grid grid-cols-2 gap-1 p-2">
              {submenuMobile.map((item) => {
                const ativo = usandoCatalogoFollowZap ? itemSubmenuFollowZapAtivo(pathname, item) : submenuDriver ? itemDriverAtivo(pathname, item.href) : pathname === item.href;
                return (
                  <Link
                    key={item.href}
                    href={hrefNavegacao(item.href)}
                    prefetch
                    aria-current={ativo ? "page" : undefined}
                    className={`flex h-9 items-center gap-2 rounded-lg px-3 text-xs font-medium transition-colors ${
                      ativo
                        ? pilotoMobileSidebar ? "bg-[var(--fz-sidebar-active-bg)] text-[var(--fz-sidebar-active-text)]" : "bg-blue-50 text-brand"
                        : pilotoMobileSidebar ? "text-[var(--fz-sidebar-text)] hover:bg-[var(--fz-sidebar-hover-bg)]" : "text-muted hover:bg-surface hover:text-navy"
                    }`}
                  >
                    <item.Icon className={`h-3.5 w-3.5 ${ativo ? pilotoMobileSidebar ? "text-[var(--fz-sidebar-active-icon)]" : "text-brand" : pilotoMobileSidebar ? "text-[var(--fz-sidebar-icon)]" : "text-muted"}`} />
                    <span className="truncate">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        ) : null}
        <div className="flex items-center justify-around px-1 py-2">
          {itensMobile.map(({ href, label, mobileLabel, Icon }) => {
            const ativo = href === hrefAtivo;
            const mostrarPonto = temNovidade && href === badgeHref;

            if (pilotoMobileSidebar) {
              return (
                <NavigationItem
                  key={href}
                  mobile
                  href={hrefNavegacao(href)}
                  label={mobileLabel ?? label}
                  Icon={Icon}
                  active={ativo}
                  indicator={mostrarPonto}
                />
              );
            }

            return (
              <Link key={href} href={hrefNavegacao(href)} prefetch className="flex flex-1 flex-col items-center gap-0.5 py-1">
                <span
                  className={`relative flex h-7 w-10 items-center justify-center rounded-xl transition-colors ${
                    ativo ? "bg-blue-50" : ""
                  }`}
                >
                  <Icon className={`h-5 w-5 transition-colors ${ativo ? "text-brand" : "text-muted"}`} />
                  {mostrarPonto && (
                    <span className="absolute right-1 top-0 h-2.5 w-2.5 rounded-full border-2 border-card bg-red-500" />
                  )}
                </span>
                <span className={`text-[10px] font-medium transition-colors ${ativo ? "text-brand" : "text-muted"}`}>
                  {mobileLabel ?? label}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>
    </>
  );
}
